import { supabase } from './supabase';

export type McpConfigDashboardData = {
  activeDirectives: number;
  activeBriefs: number;
  activeWorkflows: number;
  revisionsLast7Days: number;
  lastUpdatedAt: string | null;
  recentDirectives: Array<{ key: string; scope: string; directive: string }>;
  hostedMcpUrl: string;
};

const HOSTED_MCP_URL = 'https://mcp.stephansmith.me/mcp';

function isMissingTableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const msg = String((error as { message?: string }).message ?? '').toLowerCase();
  const code = String((error as { code?: string }).code ?? '');
  return (
    code === '42P01' ||
    msg.includes('does not exist') ||
    msg.includes('agent_directives') ||
    msg.includes('agent_workflows') ||
    msg.includes('schema cache')
  );
}

export async function fetchMcpConfigDashboardData(): Promise<McpConfigDashboardData> {
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const [directivesRes, briefsRes, workflowsRes, revisionsRes, recentRes] = await Promise.all([
    supabase
      .from('agent_directives')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'active'),
    supabase
      .from('agent_context_briefs')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'active'),
    supabase
      .from('agent_workflows')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'active'),
    supabase
      .from('agent_directive_revisions')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', sevenDaysAgo),
    supabase
      .from('agent_directives')
      .select('key, scope, directive, updated_at')
      .eq('status', 'active')
      .order('priority', { ascending: true })
      .order('updated_at', { ascending: false })
      .limit(3),
  ]);

  const firstError =
    directivesRes.error ??
    briefsRes.error ??
    workflowsRes.error ??
    revisionsRes.error ??
    recentRes.error;
  if (firstError) {
    if (isMissingTableError(firstError)) {
      throw new Error('MCP tables not deployed yet — run supabase db push from engine.');
    }
    throw firstError;
  }

  const recent = (recentRes.data ?? []) as Array<{
    key: string;
    scope: string;
    directive: string;
    updated_at: string;
  }>;

  const lastUpdatedAt =
    recent.length > 0
      ? recent.reduce((latest, row) => {
          return row.updated_at > latest ? row.updated_at : latest;
        }, recent[0].updated_at)
      : null;

  return {
    activeDirectives: directivesRes.count ?? 0,
    activeBriefs: briefsRes.count ?? 0,
    activeWorkflows: workflowsRes.count ?? 0,
    revisionsLast7Days: revisionsRes.count ?? 0,
    lastUpdatedAt,
    recentDirectives: recent.map((r) => ({
      key: r.key,
      scope: r.scope,
      directive: r.directive,
    })),
    hostedMcpUrl: HOSTED_MCP_URL,
  };
}
